import React, { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  X,
  Layers
} from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { MultiSelect } from "../components/ui/multi-select";
import useToast from "../hooks/useToast";
import { uploadFileToS3 } from "../services/uploadService";
import { getCategories } from "../services/categoryService";
import {
  createProfessor,
  deleteProfessor,
  getProfessors,
  updateProfessor
} from "../services/professorService";

const professorSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  designation: z.string().trim().min(1, "Designation is required"),
  qualification: z.string().trim().optional(),
  aebas_attendance_id: z.string().trim().optional(),
  mbbs_reg_number: z.string().trim().optional(),
  mdms_reg_number: z.string().trim().optional(),
  date_of_promotion: z.string().trim().optional(),
  other_reg_number: z.string().trim().optional(),
  total_experiance: z.string().trim().optional(),
  exp_2026: z.string().trim().optional(),
  exp_2025: z.string().trim().optional(),
  exp_2024: z.string().trim().optional(),
  exp_2023: z.string().trim().optional(),
  exp_2022: z.string().trim().optional(),
  number_of_lectures: z.string().trim().optional(),
  bcbr_passing_year: z.string().trim().optional(),
  bcme_training_year: z.string().trim().optional(),
  profile_image: z.string().trim().optional(),
  short_description: z.string().trim().optional(),
  category_ids: z.array(z.string()).optional()
});

const defaultValues = {
  name: "",
  designation: "",
  qualification: "",
  aebas_attendance_id: "",
  mbbs_reg_number: "",
  mdms_reg_number: "",
  date_of_promotion: "",
  other_reg_number: "",
  total_experiance: "",
  exp_2026: "",
  exp_2025: "",
  exp_2024: "",
  exp_2023: "",
  exp_2022: "",
  number_of_lectures: "",
  bcbr_passing_year: "",
  bcme_training_year: "",
  profile_image: "",
  short_description: "",
  category_ids: []
};

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function truncateText(text = "", maxLength = 90) {
  if (!text) {
    return "--";
  }

  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "...", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

export default function Professor() {
  const [professors, setProfessors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDeletingProfessor, setIsDeletingProfessor] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProfessorId, setEditingProfessorId] = useState(null);
  const [professorPendingDelete, setProfessorPendingDelete] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 1
  });
  const fileInputRef = useRef(null);
  const { showErrorToast, showSuccessToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues,
    resolver: zodResolver(professorSchema)
  });

  const profileImage = watch("profile_image");
  const previewName = watch("name");

  const refreshListing = (targetPage = pagination.page, targetLimit = pagination.limit) => {
    if (targetPage !== pagination.page || targetLimit !== pagination.limit) {
      setPagination((current) => ({
        ...current,
        page: targetPage,
        limit: targetLimit
      }));
      return;
    }

    loadProfessors(targetPage, targetLimit);
  };

  async function loadInitialData() {
    const response = await getCategories({ limit: 100 });
    if (response?.success) {
      setCategories(response.data.categories || []);
    }
  }

  async function loadProfessors(pageArg = pagination.page, limitArg = pagination.limit) {
    setIsLoading(true);

    const response = await getProfessors({
      page: pageArg,
      limit: limitArg
    });

    if (response?.success) {
      setProfessors(response.data.professors || []);
      setPagination({
        page: response.data.pagination?.page || pageArg,
        limit: response.data.pagination?.limit || limitArg,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1
      });
    } else {
      showErrorToast(response?.message || "Unable to load professors");
    }

    setIsLoading(false);
  }

  useEffect(() => {
    loadInitialData();
    loadProfessors(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  const openCreateModal = () => {
    reset(defaultValues);
    setEditingProfessorId(null);
    setIsDragActive(false);
    setIsUploadingImage(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    reset(defaultValues);
    setEditingProfessorId(null);
    setIsDragActive(false);
    setIsUploadingImage(false);
    setIsModalOpen(false);
  };

  const openDeleteModal = (professor) => {
    setProfessorPendingDelete(professor);
  };

  const closeDeleteModal = () => {
    if (isDeletingProfessor) {
      return;
    }

    setProfessorPendingDelete(null);
  };

  const handleFileRead = (file) => {
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      showErrorToast("Please upload an image file");
      return;
    }

    setIsUploadingImage(true);

    uploadFileToS3(file, "professors").then((response) => {
      if (!response?.success) {
        showErrorToast(response?.message || "Unable to upload image");
        setIsUploadingImage(false);
        return;
      }

      setValue("profile_image", response.data.file.url, {
        shouldDirty: true,
        shouldValidate: true
      });
      showSuccessToast("Image uploaded successfully");
      setIsUploadingImage(false);
    });
  };

  const handleFileSelect = (event) => {
    handleFileRead(event.target.files?.[0]);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);
    handleFileRead(event.dataTransfer.files?.[0]);
  };

  const handleEdit = (professor) => {
    setEditingProfessorId(professor.id);
    reset({
      name: professor.name,
      designation: professor.designation,
      qualification: professor.qualification || "",
      aebas_attendance_id: professor.aebas_attendance_id || "",
      mbbs_reg_number: professor.mbbs_reg_number || "",
      mdms_reg_number: professor.mdms_reg_number || "",
      date_of_promotion: professor.date_of_promotion || "",
      other_reg_number: professor.other_reg_number || "",
      total_experiance: professor.total_experiance || "",
      exp_2026: professor.exp_2026 || "",
      exp_2025: professor.exp_2025 || "",
      exp_2024: professor.exp_2024 || "",
      exp_2023: professor.exp_2023 || "",
      exp_2022: professor.exp_2022 || "",
      number_of_lectures: professor.number_of_lectures || "",
      bcbr_passing_year: professor.bcbr_passing_year || "",
      bcme_training_year: professor.bcme_training_year || "",
      profile_image: professor.profile_image || "",
      short_description: professor.short_description || "",
      category_ids: (professor.departments || []).map(c => String(c.id))
    });
    setIsDragActive(false);
    setIsUploadingImage(false);
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!professorPendingDelete) {
      return;
    }

    setIsDeletingProfessor(true);

    const response = await deleteProfessor(professorPendingDelete.id);

    setIsDeletingProfessor(false);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to delete professor");
      return;
    }

    const nextPage = professors.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
    showSuccessToast(response.message || "Professor deleted successfully");
    closeDeleteModal();
    refreshListing(nextPage, pagination.limit);
  };

  const onSubmit = async (values) => {
    const payload = {
      ...values,
      profile_image: values.profile_image || "",
      short_description: values.short_description || "",
      category_ids: values.category_ids?.map(Number) || []
    };

    const response = editingProfessorId
      ? await updateProfessor(editingProfessorId, payload)
      : await createProfessor(payload);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to save professor");
      return;
    }

    showSuccessToast(response.message || "Professor saved successfully");
    closeModal();

    if (editingProfessorId) {
      refreshListing(pagination.page, pagination.limit);
      return;
    }

    refreshListing(1, pagination.limit);
  };

  const startItem = pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem = pagination.total ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const pageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Faculty Module"
        title="Professor"
        description="Manage professor records and their associated sub departments."
        badge={`${pagination.total} Profiles`}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Professor Listing</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">
              View professor records with assigned sub departments.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <GraduationCap className="h-5 w-5" />
            </div>
            <Button type="button" onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" />
              Add Professor
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="rounded-3xl border border-slate-100 bg-slate-50/80 p-6 text-sm text-panel-muted">
              Loading professor records...
            </div>
          ) : professors.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="bg-slate-50/90">
                      <tr className="text-left">
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Profile</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Name</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Designation</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Sub departments</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {professors.map((professor) => (
                        <tr key={professor.id} className="border-t border-slate-100 align-top">
                          <td className="px-5 py-4">
                            {professor.profile_image ? (
                              <img
                                src={professor.profile_image}
                                alt={professor.name}
                                className="h-14 w-14 rounded-2xl object-cover"
                              />
                            ) : (
                              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-sm font-bold text-white">
                                {getInitials(professor.name)}
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-4 text-sm font-semibold text-panel-ink">{professor.name}</td>
                          <td className="px-5 py-4 text-sm text-primary">{professor.designation}</td>
                          <td className="px-5 py-4">
                            <div className="flex flex-wrap gap-1">
                              {professor.departments?.length > 0 ? (
                                professor.departments.map(cat => (
                                  <Badge key={cat.id} variant="secondary" className="text-[10px] py-0">{cat.name}</Badge>
                                ))
                              ) : (
                                <span className="text-xs text-panel-muted italic">No sub department</span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-4 text-right">
                            <div className="flex gap-2">
                              <Button type="button" size="sm" variant="outline" onClick={() => handleEdit(professor)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </Button>
                              <Button type="button" size="sm" variant="ghost" className="hover:bg-rose-50 hover:text-rose-600 text-panel-muted" onClick={() => openDeleteModal(professor)}>
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-slate-50/70 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-panel-muted">
                  Showing <span className="font-semibold text-panel-ink">{startItem}</span> to{" "}
                  <span className="font-semibold text-panel-ink">{endItem}</span> of{" "}
                  <span className="font-semibold text-panel-ink">{pagination.total}</span> professors
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-panel-muted">
                    Rows
                    <select
                      className="h-10 rounded-2xl border border-slate-200 bg-white px-3 text-sm text-panel-ink outline-none"
                      value={pagination.limit}
                      onChange={(event) =>
                        setPagination((current) => ({
                          ...current,
                          page: 1,
                          limit: Number(event.target.value)
                        }))
                      }
                    >
                      {[5, 10, 20].map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() =>
                        setPagination((current) => ({
                          ...current,
                          page: current.page - 1
                        }))
                      }
                    >
                      <ChevronLeft className="mr-1 h-4 w-4" />
                      Prev
                    </Button>
                    <div className="flex flex-wrap items-center gap-2">
                      {pageNumbers.map((item, index) =>
                        item === "..." ? (
                          <span key={`ellipsis-${index}`} className="px-1 text-sm font-medium text-panel-muted">
                            ...
                          </span>
                        ) : (
                          <Button
                            key={item}
                            type="button"
                            size="sm"
                            variant={pagination.page === item ? "default" : "outline"}
                            onClick={() =>
                              setPagination((current) => ({
                                ...current,
                                page: item
                              }))
                            }
                          >
                            {item}
                          </Button>
                        )
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() =>
                        setPagination((current) => ({
                          ...current,
                          page: current.page + 1
                        }))
                      }
                    >
                      Next
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50/80 p-8 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Sparkles className="h-6 w-6" />
              </div>
              <p className="mt-4 text-lg font-semibold text-panel-ink">No professors added yet</p>
              <p className="mt-2 text-sm text-panel-muted">
                Add your first professor record with name, designation, profile image, and short description.
              </p>
              <Button type="button" className="mt-5" onClick={openCreateModal}>
                <Plus className="mr-2 h-4 w-4" />
                Add Professor
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {isModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4">
          <div className="max-h-[90dvh] w-full max-w-4xl overflow-y-auto rounded-[32px] border border-white/80 bg-white shadow-[0_24px_80px_rgba(44,33,111,0.22)]">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/95 px-6 py-5 backdrop-blur">
              <div>
                <h2 className="text-2xl font-bold text-panel-ink">
                  {editingProfessorId ? "Edit Professor" : "Add Professor"}
                </h2>
                <p className="mt-1 text-sm text-panel-muted">
                  Name and designation are mandatory. Profile image uses drag-and-drop upload.
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-panel-muted transition-colors hover:bg-slate-100 hover:text-panel-ink"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid gap-5 p-6 xl:grid-cols-[340px_1fr]">
              <div className="space-y-4">
                <div
                  className={`rounded-3xl border-2 border-dashed p-5 transition-colors ${
                    isDragActive ? "border-primary bg-primary/5" : "border-slate-200 bg-slate-50/70"
                  }`}
                  onDragEnter={(event) => {
                    event.preventDefault();
                    setIsDragActive(true);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragActive(true);
                  }}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    setIsDragActive(false);
                  }}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelect}
                  />

                  <div className="flex flex-col items-center text-center">
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt={previewName || "Professor preview"}
                        className="h-36 w-36 rounded-[28px] object-cover shadow-sm"
                      />
                    ) : (
                      <div className="flex h-36 w-36 items-center justify-center rounded-[28px] bg-primary text-3xl font-bold text-white shadow-sm">
                        {getInitials(previewName || "PR")}
                      </div>
                    )}

                    <p className="mt-4 text-base font-semibold text-panel-ink">Profile Image Upload</p>
                    <p className="mt-2 max-w-xs text-sm leading-6 text-panel-muted">
                      Drag and drop an image here, or click the button below to upload.
                    </p>

                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                        <Upload className="mr-2 h-4 w-4" />
                        {isUploadingImage ? "Uploading..." : "Upload Image"}
                      </Button>
                      {profileImage ? (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() =>
                            setValue("profile_image", "", {
                              shouldDirty: true,
                              shouldValidate: true
                            })
                          }
                          disabled={isUploadingImage}
                        >
                          <X className="mr-2 h-4 w-4" />
                          Remove
                        </Button>
                      ) : null}
                    </div>
                    {isUploadingImage ? (
                      <p className="mt-3 text-sm font-medium text-primary">Uploading to AWS S3...</p>
                    ) : null}
                  </div>
                </div>
              </div>

              <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
                <input type="hidden" {...register("profile_image")} />

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <FieldLabel>Name *</FieldLabel>
                    <Input placeholder="Enter professor name" {...register("name")} />
                    {errors.name ? <FieldError>{errors.name.message}</FieldError> : null}
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>Designation *</FieldLabel>
                    <Input placeholder="Enter designation (e.g. ASSISTANT PROFESSOR)" {...register("designation")} />
                    {errors.designation ? <FieldError>{errors.designation.message}</FieldError> : null}
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 border-t pt-4">
                  <h3 className="col-span-2 font-bold text-panel-ink text-sm">Professional Details</h3>
                  
                  <div className="space-y-2">
                    <FieldLabel>Qualification</FieldLabel>
                    <Input placeholder="E.g. MBBS, MD" {...register("qualification")} />
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>AEBAS Attendance ID</FieldLabel>
                    <Input placeholder="E.g. 10123" {...register("aebas_attendance_id")} />
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>MBBS Reg. Number</FieldLabel>
                    <Input placeholder="E.g. G-12345" {...register("mbbs_reg_number")} />
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>MD/MS Reg. Number</FieldLabel>
                    <Input placeholder="E.g. G-67890" {...register("mdms_reg_number")} />
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>Date of Promotion</FieldLabel>
                    <Input placeholder="E.g. 01/01/2026" {...register("date_of_promotion")} />
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>Other Reg. Number (BDS/MDS)</FieldLabel>
                    <Input placeholder="E.g. BDS-987" {...register("other_reg_number")} />
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>Total Teaching Experience</FieldLabel>
                    <Input placeholder="E.g. 10 Years" {...register("total_experiance")} />
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>Number of Lectures</FieldLabel>
                    <Input placeholder="E.g. 45" {...register("number_of_lectures")} />
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>BCBR Passing Year</FieldLabel>
                    <Input placeholder="E.g. 2024" {...register("bcbr_passing_year")} />
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>BCME Training Year</FieldLabel>
                    <Input placeholder="E.g. 2025" {...register("bcme_training_year")} />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-5 border-t pt-4">
                  <h3 className="col-span-5 font-bold text-panel-ink text-sm">Details of Service (Last 5 Years)</h3>
                  
                  <div className="space-y-2">
                    <FieldLabel>Exp 2022</FieldLabel>
                    <Input placeholder="2022" {...register("exp_2022")} />
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>Exp 2023</FieldLabel>
                    <Input placeholder="2023" {...register("exp_2023")} />
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>Exp 2024</FieldLabel>
                    <Input placeholder="2024" {...register("exp_2024")} />
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>Exp 2025</FieldLabel>
                    <Input placeholder="2025" {...register("exp_2025")} />
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>Exp 2026</FieldLabel>
                    <Input placeholder="2026" {...register("exp_2026")} />
                  </div>
                </div>

                <div className="space-y-2 text-panel-ink border-t pt-4">
                  <FieldLabel className="flex items-center gap-2"><Layers size={16} /> Assign Sub department</FieldLabel>
                  <Controller
                    name="category_ids"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        options={categories}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Search and select sub departments..."
                      />
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel>Short Description</FieldLabel>
                  <Textarea placeholder="Write a short description" {...register("short_description")} />
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button type="submit" disabled={isSubmitting || isUploadingImage}>
                    {isSubmitting ? "Saving..." : editingProfessorId ? "Update Professor" : "Save Professor"}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeModal}>
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      {professorPendingDelete ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-[32px] border border-white/80 bg-white p-6 shadow-[0_24px_80px_rgba(44,33,111,0.22)]">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
              <Trash2 className="h-5 w-5" />
            </div>
            <h2 className="mt-5 text-2xl font-bold text-panel-ink">Delete Professor</h2>
            <p className="mt-3 text-sm leading-6 text-panel-muted">
              Are you sure you want to delete <span className="font-semibold text-panel-ink">{professorPendingDelete.name}</span>?
              This action will remove the record from the listing.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button type="button" variant="destructive" onClick={handleDelete} disabled={isDeletingProfessor}>
                {isDeletingProfessor ? "Deleting..." : "Delete Professor"}
              </Button>
              <Button type="button" variant="outline" onClick={closeDeleteModal} disabled={isDeletingProfessor}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
